import { useEffect, useMemo, useRef, useState } from 'react';

import { addressPathLabel, searchAddresses, type AddressSearchResult } from '../../lib/addressApi';
import { Select } from './Select';
import type { SelectOption } from './selectShared';

/** The three administrative rungs, stored as the NAMES they were picked under. */
export interface AddressValue {
  province: string | null;
  cityMunicipality: string | null;
  barangay: string | null;
}

const BLANK: AddressValue = { province: null, cityMunicipality: null, barangay: null };

/** Long enough that a reader is not fetching on every keystroke, short enough to feel live. */
const DEBOUNCE_MS = 250;

/**
 * ONE control for a whole Philippine address — province, city or municipality, and barangay.
 *
 * **Not a cascade, and that is the point.** Three chained dropdowns ask the reader to answer
 * the schema's question — which region, then which province, then which city — before they can
 * state the one thing they actually know, which is where they live. This searches every level
 * at once: typing `antipolo` offers `Rizal, Antipolo`, and picking it binds all three rungs in
 * one action.
 *
 * **A result may stop short of a barangay, and that is a valid answer.** Barangays have no bulk
 * source in this catalog yet, so most places bottom out at the city; a picker that refused
 * anything less than a full path would be unusable against the data that exists.
 *
 * **It writes names, never ids.** An address is what someone STATED at a point in time, so
 * renaming a city in the catalog must not rewrite the addresses already collected — the
 * snapshot-versus-live-view rule. The catalog's job ends at helping the reader choose.
 *
 * It is a composition over the shared `<Select>`, not a second dropdown: the trigger is
 * already the search box, `onQueryChange` already drives a remote feed, and `sublabel` already
 * draws the second line. Re-implementing the panel, the keyboard handling and the portal would
 * be the drift the shared-input catalogue exists to prevent.
 */
export function AddressPicker({
  id,
  label = 'Province / City / Municipality / Barangay',
  description,
  value,
  onChange,
  required = false,
  error,
  disabled = false,
  search = searchAddresses,
}: {
  id: string;
  label?: string;
  description?: string;
  value: AddressValue;
  onChange: (next: AddressValue) => void;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  /**
   * The feed the picker searches. The default is the catalog's general search, on
   * `personnel.read`; a surface whose readers do not hold that key passes its OWN feed over the
   * same catalog (Self Service's Start training). A STABLE reference — a module-level function.
   */
  search?: (query: string, signal?: AbortSignal) => Promise<AddressSearchResult[]>;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<AddressSearchResult[]>([]);
  const [loading, setLoading] = useState(false);

  const bound = addressPathLabel(value);

  useEffect(() => {
    const term = query.trim();

    if (term.length === 0) {
      setResults([]);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(() => {
      setLoading(true);

      search(term, controller.signal)
        .then((next) => {
          if (!controller.signal.aborted) setResults(next);
        })
        .catch(() => {
          // A failed lookup is an empty list, never a broken form: the reader can still type
          // again, and the rest of the address is theirs to fill in.
          if (!controller.signal.aborted) setResults([]);
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query, search]);

  /*
    The bound value is injected as its own option. `<Select>` resolves a label by finding the
    value among its options, and the search feed does not contain what was picked a moment ago
    — without this the field would show its placeholder on a record that has an address.
  */
  const options = useMemo<SelectOption[]>(() => {
    const fromResults = results.map((r) => ({
      value: r.key,
      label: r.barangay ?? r.cityMunicipality ?? r.province,
      sublabel: addressPathLabel({
        province: r.province,
        cityMunicipality: r.cityMunicipality,
        barangay: r.barangay,
      }),
    }));

    if (!bound) return fromResults;

    return [
      { value: BOUND_KEY, label: bound },
      ...fromResults.filter((o) => o.value !== BOUND_KEY),
    ];
  }, [results, bound]);

  // Held so a pick can be resolved back to its parts without searching the list again.
  const byKey = useRef(new Map<string, AddressSearchResult>());
  byKey.current = new Map(results.map((r) => [r.key, r]));

  return (
    <Select
      id={id}
      label={label}
      description={description}
      required={required}
      value={bound ? BOUND_KEY : null}
      onChange={(next) => {
        if (next === null) {
          // Cleared: the whole path goes, not just its last rung. A city with no province is
          // not half an address, it is a broken one.
          onChange(BLANK);
          return;
        }

        const picked = byKey.current.get(next);

        if (picked) {
          onChange({
            province: picked.province,
            cityMunicipality: picked.cityMunicipality,
            barangay: picked.barangay,
          });
        }
      }}
      options={options}
      /*
        `remote` draws the search affordance; `serverFiltered` is what stops the panel
        filtering again. They are separate props because they are separate things — and this
        picker is the case that proves it: the server matches across the WHOLE path, so a
        second pass over the label alone drops `Rizal, Antipolo` as soon as a reader types
        both words, and the panel says "no matches" for a query that matched.
      */
      remote
      serverFiltered
      onQueryChange={setQuery}
      loading={loading}
      sort="none"
      placeholder="Search province, city, or barangay…"
      /*
        Passed straight down: <Select> is the control the reader touches, so it owns the
        deferral that keeps a required-but-empty field from opening red. See useDeferredError.
      */
      error={error}
      disabled={disabled}
    />
  );
}

/**
 * The option key standing for whatever is currently bound.
 *
 * A constant rather than the place's own id, because an address stores NAMES — the id it was
 * picked under is not kept, so there is nothing to match against on a record loaded from the
 * server.
 */
const BOUND_KEY = '__bound__';
